/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import DocAPI from "./DocAPI";
import { isUndefined, isString, extend, noop } from "utils/lodashUtils";
import { providers } from "dc-core";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";

import { AJSMetadataStatus } from "constants/enums";
import { ajsMakeCancellablePromise } from "dc-preview-utils/src/Utils/AJSCancellablePromise";
import { isExternalAsset as _isExternalAsset, isSignAsset as _isSignAsset } from "utils/AssetUtils";
import { roundOffTo2Decimal } from "utils/MathUtils";
import * as stores from "stores";
import {
  getEnableLinearization,
  getEnableCNPDF,
  getShouldUseModernViewer,
  getJsSAB,
  getLinearizationBlockSize
} from "selectors/appSelectors";
import { isPDF, getInitialPageLoadTime } from "selectors/documentSelectors";
import { triggerWaitingOnCompletePDFBufferEvent } from "publicApis/Events/events";
import { logMsgsAndWaitTimers } from "logging";
import { LOG_WAIT_TIMES_TIMEOUT, NEXT_MSG_WAIT_TIMEOUT } from "constants/constants";
import { getIsDemoMode } from "selectors/overlayDropinSelectors";
import { logCNPDFComponentFetchLogs } from "utils/CnpdfLoggingUtils";

const pdfCommandsResolvedByDCAPI = Object.freeze([
  "getMetadata",
  "downloadAssetContentBuffer",
  "getPage",
  "resetWorker"
]);

export default class DocAPIInDropin extends DocAPI {
  constructor() {
    super();
    this.init();
    this.dcapiMetaDataSucceeded = false;
    this.externalMetaDataSucceeded = false;
    this.disableDCAPIForPDF = false;
    this.disableAJSForPDF = false;
    this.waitTimersDataLogged = false;
    this._lastPageNum = 1;
    this.commandToTimersMap = new Map();
    this.getAcroFormFields = this.getAcroFormFields.bind(this);
    this.editAcroFormField = this.editAcroFormField.bind(this);
    this.editAcroFormFields = this.editAcroFormFields.bind(this);
    this.createAcroFormField = this.createAcroFormField.bind(this);
    this.createAcroFormFields = this.createAcroFormFields.bind(this);
    this.deleteAcroFormField = this.deleteAcroFormField.bind(this);
    this.deleteAcroFormFields = this.deleteAcroFormFields.bind(this);
    this.updateSignMetadata = this.updateSignMetadata.bind(this);
    this.getSignMetadata = this.getSignMetadata.bind(this);
    this.getDocumentParts = this.getDocumentParts.bind(this);
    this.setDocumentParts = this.setDocumentParts.bind(this);
    this.getCommentsList = this.getCommentsList.bind(this);
    this.createComment = this.createComment.bind(this);
    this.editComment = this.editComment.bind(this);
    this.deleteComment = this.deleteComment.bind(this);
    this.getContentBuffer = this.getContentBuffer.bind(this);
    this.createComments = this.createComments.bind(this);
    this.deleteComments = this.deleteComments.bind(this);
    this.createLinkAnnot = this.createLinkAnnot.bind(this);
    this.editLinkAnnot = this.editLinkAnnot.bind(this);
    this.editLinkAnnots = this.editLinkAnnots.bind(this);
    this.deleteLinkAnnot = this.deleteLinkAnnot.bind(this);
    this.deleteLinkAnnots = this.deleteLinkAnnots.bind(this);
    this.getLinkAnnots = this.getLinkAnnots.bind(this);
    this._updateMsgsForLoggingWaitTime = this._updateMsgsForLoggingWaitTime.bind(this);
    this._logMessagesAndWaitTimers = this._logMessagesAndWaitTimers.bind(this);
  }

  saveLastPageNumReceivedInMetadata(lastPageNum) {
    this._lastPageNum = lastPageNum;
  }

  setSecondaryMetaDataSucceeded(value, isExternal = false) {
    if (isExternal) {
      this.externalMetaDataSucceeded = value;
    } else {
      this.dcapiMetaDataSucceeded = value;
    }
  }

  init({ assetUri = "", assetType = "", filetype = "" } = {}) {
    this.rendition = null;
    this.assetUri = assetUri;
    this.assetType = assetType;
    this.filetype = filetype;
    this.disableDCAPIForPDF =
      getQueryParameterFromUrl(window.location.href, "disableDCAPIForPDF") === "true" ||
      _isExternalAsset(this.assetType);
    this.dcapiMetaDataSucceeded = false;
    this.externalMetaDataSucceeded = false;
    this.ajsMetadataStatus = AJSMetadataStatus.UNKNOWN;
    this.missingFontAnalytics = null;

    super.init(...arguments);
  }

  resetAssetURI(assetUri) {
    this.assetUri = assetUri;
  }

  shouldUseRasterRenditions() {
    return _isExternalAsset(this.assetType) || !this.disableDCAPIForPDF;
  }

  _shouldWaitOnCompletePDFBuffer() {
    return this.rendition
      .shouldWaitOnCompletePDFBuffer(this.assetUri)
      .then(() => triggerWaitingOnCompletePDFBufferEvent());
  }

  ready() {
    const enableExternal = _isExternalAsset(this.assetType);
    return providers
      .rendition(null, {
        enableExternal,
        useReview: !enableExternal,
        useAJSRichBuild: this.isRichBuild
      })
      .then(rendition => {
        this.rendition = rendition;
        /* optimization, trigger download before getmetadata call is triggered
      TODO: add download promise in rendition provider and then uncomment this */
        // this.getFileBuffer();
        return true;
      });
  }

  getMethodName(methodName) {
    let returnMethodName = methodName;
    switch (methodName) {
      case "getPage":
        returnMethodName = "getRendition";
        break;
      case "JSApi":
        returnMethodName = "performCommandSynchronously";
        break;
      default:
        break;
    }
    return returnMethodName;
  }

  /*
  Process ajs rendition response to replace
  1) params -> data
  2) pageImgBuffer -> buffer
  This is done to keep consistency with other ajs responses
  */
  _processAjsRendition(renditionResponse) {
    const { pageImgBuffer: buffer, ...rest } = renditionResponse.params;
    renditionResponse.data = {
      buffer,
      ...rest
    };
    delete renditionResponse.params;
    return renditionResponse;
  }

  _logMessagesAndWaitTimers() {
    const msgsToTimersObj = Object.fromEntries(this.commandToTimersMap);
    this.waitTimersDataLogged = true;
    logMsgsAndWaitTimers(msgsToTimersObj);
  }

  _getUpdatedTimeLimits(newTime, oldLimits) {
    const roundedOffNewTime = roundOffTo2Decimal(newTime);
    return {
      min: oldLimits ? Math.min(roundedOffNewTime, oldLimits.min) : roundedOffNewTime,
      max: oldLimits ? Math.max(roundedOffNewTime, oldLimits.max) : roundedOffNewTime
    };
  }

  _updateMsgsForLoggingWaitTime(message) {
    let timersToLog = {},
      timeTakenByT5,
      queueWaitTime,
      totalTime,
      count = 0;
    if (this.commandToTimersMap.has(message.command)) {
      const loggingData = this.commandToTimersMap.get(message.command);
      timeTakenByT5 = loggingData.timers.timeTakenByT5;
      queueWaitTime = loggingData.timers.queueWaitTime;
      totalTime = loggingData.timers.totalTime;
      count = loggingData.count;
    }

    timersToLog = {
      timers: {
        timeTakenByT5: this._getUpdatedTimeLimits(message.timeTaken, timeTakenByT5),
        queueWaitTime: this._getUpdatedTimeLimits(message.queueWaitTime, queueWaitTime),
        totalTime: this._getUpdatedTimeLimits(message.totalTime, totalTime)
      },
      count: count + 1
    };
    this.commandToTimersMap.set(message.command, timersToLog);
    clearTimeout(this.logMessagesAndWaitTimersTimeout);
    this.logMessagesAndWaitTimersTimeout = setTimeout(this._logMessagesAndWaitTimers, NEXT_MSG_WAIT_TIMEOUT);
  }

  _dispatch(messageObj, isPreload = false) {
    if (!isUndefined(messageObj) && isString(messageObj.command) && this.rendition) {
      let methodName = this.getMethodName(messageObj.command);
      if (!isString(methodName) || !this.rendition[methodName]) {
        methodName = "performCommand";
      }

      let { useAJS, useDCAPI, enableExternal } = this._getRenditionFlags(methodName, messageObj.command);

      if (methodName === "getRendition" && this.getAJSMetaDataSucceeded()) {
        enableExternal = false;
      }

      if (isPreload) {
        useAJS = true;
      }

      const initialPageLoadTime = getInitialPageLoadTime(stores);
      let shouldLogQueueWaitTime = false;
      if (initialPageLoadTime && Date.now() - initialPageLoadTime <= LOG_WAIT_TIMES_TIMEOUT) {
        shouldLogQueueWaitTime = true;
      }

      let response = this.rendition[methodName](
        decodeURIComponent(this.assetUri),
        extend(messageObj.params, {
          commandName: messageObj.command,
          useAJS,
          useDCAPI,
          enableExternal,
          assetMimeType: this.filetype,
          assetType: this.assetType
        })
      );

      if (response.ajs && response.external) {
        response.ajs = ajsMakeCancellablePromise(response.ajs);
        response.external = ajsMakeCancellablePromise(response.external);
      } else if (response.ajs && response.dcapi) {
        response.ajs = ajsMakeCancellablePromise(response.ajs);
        response.dcapi = ajsMakeCancellablePromise(response.dcapi);
      } else if (response instanceof Promise) {
        response = ajsMakeCancellablePromise(response);
      }
      // since response can be instance of promise and may not contain ajs and dcapi promises
      if (response.ajs) {
        response.ajs.catch(noop);
      }
      if (response.dcapi) {
        response.dcapi.catch(noop);
      }

      if (response.external) {
        response.external.catch(noop);
      }

      if (response.ajs) {
        response.ajs
          .then(resp => {
            logCNPDFComponentFetchLogs(resp);
            if (!this.waitTimersDataLogged && shouldLogQueueWaitTime) {
              this._updateMsgsForLoggingWaitTime(resp);
            }
          })
          .catch(() => {});
      }

      if (methodName === "performCommand") return response.ajs;
      if (methodName === "getRendition" && response.ajs) {
        response.ajs.then(this._processAjsRendition).catch(() => {});
      }
      return response;
    }
    return ajsMakeCancellablePromise(Promise.reject()).catch(noop);
  }

  _getRenditionFlags(methodName, commandName = methodName) {
    let useDCAPI = true;
    let useAJS = true;
    let enableExternal = _isExternalAsset(this.assetType);
    if (
      !isString(methodName) ||
      !this.rendition ||
      !this.rendition[methodName] ||
      (this.getAJSMetaDataSucceeded() && methodName === "getRendition") ||
      pdfCommandsResolvedByDCAPI.indexOf(commandName) === -1 ||
      (commandName === "resetWorker" && this.assetType === "/review") ||
      (this.disableDCAPIForPDF && this.filetype === "application/pdf" && methodName === "getRendition") ||
      (this.filetype === "application/pdf" && methodName === "getContentBuffer")
    ) {
      useDCAPI = false;
    }
    if (
      (this.filetype !== "application/pdf" && this.filetype !== "undefined") ||
      (methodName !== "getMetadata" &&
        methodName !== "downloadAssetContentBuffer" &&
        !(
          (methodName === "performCommand" && (commandName === "resetWorker" || commandName === "getPageRangeGeometry")) // To use getPageRangeGeometry via performCommand
        ) &&
        !this.getAJSMetaDataSucceeded()) ||
      this.disableAJSForPDF
    ) {
      useAJS = false;
    }
    if (enableExternal || _isSignAsset(this.assetType)) {
      useDCAPI = false;
    }
    return {
      useDCAPI,
      useAJS,
      enableExternal
    };
  }

  isCompletePDFBufferAvailable() {
    return this.rendition.isCompletePDFBufferAvailable(this.assetUri);
  }

  isCompleteCNPDFAvailable() {
    if (this.rendition.isCompleteCNPDFAvailable) {
      return this.rendition.isCompleteCNPDFAvailable(this.assetUri).then(() => true);
    }
    return Promise.resolve(false);
  }

  getMetaData({ password = "", forceCacheless = false, _useAJS }) {
    let { useDCAPI, useAJS, enableExternal } = this._getRenditionFlags("getMetadata");
    if (!isUndefined(_useAJS)) {
      useAJS = _useAJS;
    }
    let response = this.rendition.getMetadata(decodeURIComponent(this.assetUri), {
      assetMimeType: this.filetype,
      assetType: this.assetType,
      password,
      includeActions: true,
      jsSAB: getJsSAB(stores),
      forceCacheless,
      useAJS,
      useDCAPI,
      enableExternal,
      enableLinearization: getEnableLinearization(stores),
      enableCNPDF: getEnableCNPDF(stores),
      shouldUseModernViewer: getShouldUseModernViewer(stores),
      linearizationBlockSize: getLinearizationBlockSize(stores),
      storePDFHash: getIsDemoMode(stores)
    });
    this._shouldWaitOnCompletePDFBuffer();

    response.ajs = ajsMakeCancellablePromise(response.ajs);
    response.ajs.then(resp => {
      logCNPDFComponentFetchLogs(resp);
    });
    response.dcapi = ajsMakeCancellablePromise(response.dcapi);
    if (enableExternal) {
      response.external = ajsMakeCancellablePromise(response.external);
    }
    return response;
  }

  getMetaDataFromDCAPI({ assetUri = "", enableRetryForPDFs = false, forceCacheless }) {
    let response = this.rendition.getMetadata(decodeURIComponent(assetUri), {
      useAJS: false,
      useDCAPI: true,
      enableExternal: false,
      enableRetryForPDFs,
      forceCacheless,
      shouldUseModernViewer: getShouldUseModernViewer(stores)
    });
    response.ajs.catch(noop);
    return response.dcapi;
  }

  async getMetaDataFromAJS(params = {}) {
    let response = await this.rendition.performCommand(
      decodeURIComponent(this.assetUri),
      Object.assign(
        {
          commandName: "getMetadata",
          useAJS: true,
          useDCAPI: false,
          enableExternal: false
        },
        params
      )
    );
    response.dcapi.catch(noop);
    response.ajs.then(resp => {
      logCNPDFComponentFetchLogs(resp);
    });
    return response.ajs;
  }

  getFileBuffer() {
    let { useDCAPI, useAJS, enableExternal } = this._getRenditionFlags("downloadAssetContentBuffer");
    const downloadAssetContentBuffer = () =>
      this.rendition.downloadAssetContentBuffer(decodeURIComponent(this.assetUri), {
        assetMimeType: this.filetype,
        assetType: this.assetType,
        useAJS,
        useDCAPI,
        enableExternal
      });
    let response = isPDF(stores)
      ? this.isCompletePDFBufferAvailable().then(downloadAssetContentBuffer)
      : downloadAssetContentBuffer();

    response = ajsMakeCancellablePromise(response);
    return response;
  }

  getAssetUri() {
    return this.assetUri || "";
  }

  getAssetType() {
    return this.assetType || "";
  }

  clearRenditionCache({ assetUri = "", useAJS, useDCAPI, enableExternal }) {
    let response = this.rendition.clearRenditionCache(decodeURIComponent(assetUri), {
      useAJS: useAJS || false,
      useDCAPI: useDCAPI || false,
      enableExternal: enableExternal || false
    });

    return response;
  }

  remapAJSCache(newAssetUri) {
    return this.ready().then(() => {
      let promise = this._dispatch({
        command: "remapAJSCache",
        params: {
          oldAssetUri: this.assetUri,
          newAssetUri
        }
      });
      return promise.ajs;
    });
  }

  reset() {
    return this.ready().then(() => {
      let promise = this._dispatch({
        command: "resetWorker",
        params: {}
      });
      this.init();
      return promise.ajs;
    });
  }

  isExternalAsset() {
    return _isExternalAsset(this.assetType);
  }
}
